/**
 * KOA-18136 — WHICH MANAGED SCREEN BELONGS TO WHICH WORKFLOW STEP.
 *
 * The decision used to live inline in `workflowAssignScreenToUserTaskAction`, where it
 * could only be pinned at source-text level (that action needs a live React context and
 * four backend services). It is a pure function of three inputs, and it is load-bearing
 * enough to deserve its own tests: the step→screen map is what the orchestrator reads to
 * decide which task screens the Designer must style and functionalise, and what the
 * platform reads to decide whether a screen already carries a step's form.
 *
 * The live failure that moved it here (session f0d6f51c, 2026-08-17): four user tasks
 * were assigned one call per task. The platform side answers with EVERY already-managed
 * step of the project, not only the ones the call asked for — so from the second call on
 * the pairing was handed 2..N steps and exactly ONE newly created screen. The
 * "next unused screen" fallback then re-pointed an already-mapped step at the new
 * screen. Two things followed, both user-visible: the completeness read saw one task
 * with a screen (so the Designer's passes ran for one screen and left the rest
 * untouched), and the platform, finding no form for the re-pointed step on the screen it
 * was now pointed at, PREPENDED a second full form — every wizard parameter field
 * duplicated on that screen.
 *
 * The second live failure, and why the name match is now DERIVED rather than folded
 * (session 379e6653, 2026-08-19): five user tasks, one call, and the whole set came back
 * ROTATED — the department-manager step was pointed at the factory-manager screen, the
 * factory-manager step at the workshop screen, and the workshop step at the department
 * screen. Two causes, compounding:
 *
 *   1. the fold could not match three of the five names. The platform generates a
 *      managed screen's name with `GenerateValidScreenName` → `ASCIIfy` →
 *      `ReplaceSpecialLettersForTechnicalName`, and that chain DELETES the Turkish
 *      dotless `ı`: `ASCIIfy` is NFD + drop-combining-marks, and `ı` (U+0131) has no
 *      decomposition, so it survives to the `a-z` filter, becomes `_`, collapses into
 *      the neighbouring space and disappears. "Başvuru Departmanı Yönetici Onayı"
 *      becomes the screen "Basvuru Departman Yonetici Onay". A fold that maps `ı` → `i`
 *      — the correct fold for every other purpose — can therefore NEVER equal the
 *      generated name. Only the two step names with no `ı` in them matched.
 *   2. the fallback ran INTERLEAVED with the name match, one step at a time, so an
 *      unmatched step consumed the screen a LATER step would have matched by name. The
 *      factory-manager step (unmatched) took the workshop screen before the workshop
 *      step was reached.
 *
 * So the matching side is no longer a fold of the step name: it GENERATES the name the
 * platform would generate and compares that. And the fallback is a separate phase that
 * only ever sees screens no step could claim by name.
 */

/** A screen created by the assignment call: the only screens a step may be paired with. */
export interface CreatedScreenLike {
  id: string;
  name: string;
}

/** One step this call ASKED to create a managed screen for. */
export interface StepAwaitingScreen {
  processId: string;
  stepId: string;
  stepName: string;
}

/** An existing `project.processStepScreenIds` entry. */
export interface StepScreenLinkLike {
  processId?: string;
  stepId?: string;
  screenId?: string;
}

/**
 * How a pairing was decided. Evidence, not a label: `order` means nothing about this
 * screen says it is that step's screen, so every consumer that would act ON the step's
 * behalf (the requirement a task screen is built from) must treat it as unproven.
 */
export type StepScreenPairingEvidence = "name" | "order";

/** One decided step↔screen pair. */
export interface StepScreenPairing {
  processId: string;
  stepId: string;
  stepName: string;
  screenId: string;
  screenName: string;
  pairedBy: StepScreenPairingEvidence;
}

/**
 * Fold a step / screen name to a comparable key — case, spacing and punctuation
 * insensitive, and identical under any locale (explicit Turkish letter mapping, never
 * `toLocaleLowerCase`).
 *
 * This is the LAST step of a comparison, never the whole of one: it is applied to the
 * generated name (see {@link generatedScreenNameForStep}), not to the raw step name.
 * Applied to a raw step name it maps `ı` → `i` while the platform deletes it, which is
 * exactly the mismatch described in this module's header.
 *
 * NOT INTERCHANGEABLE with `normalizeForSearch` (`shared/utility/search-normalize.ts`),
 * which looks like it answers the same question and does not. That one builds a SEARCH KEY
 * for type-to-filter over user-named things, and it leaves the dotless `ı` alone because
 * a person typing `ı` means `ı`. This one builds an IDENTITY key for two generated names,
 * where `ı` and `i` are the same letter surviving two different transliterations. Swapping
 * them silently breaks whichever side is borrowed — the step↔screen pairing has already
 * been rotated once by a name comparison that was almost right. The divergence is pinned by
 * a test; if a future cleanup "unifies" them, that test is the one that will object.
 */
export function foldForScreenNameMatch(value: string): string {
  return (value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .replace(/ğ/g, "g")
    .replace(/Ğ/g, "g")
    .replace(/ş/g, "s")
    .replace(/Ş/g, "s")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * `ASCIIfy` — NFD, drop every non-spacing mark, NFC.
 *
 * Ported verbatim from `Kuika.Common/Extensions/StringExtensions.cs`. Letters that
 * DECOMPOSE lose their mark (`ş`→`s`, `ö`→`o`, `İ`→`I`, `ğ`→`g`), and letters that do
 * not (`ı`, U+0131) pass through unchanged — which is the whole reason this port has to
 * be faithful rather than "close enough".
 */
function asciiFyLikePlatform(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .normalize("NFC");
}

/**
 * The name the PLATFORM generates for a step's managed screen, or null when this port
 * cannot reproduce it.
 *
 * Ported from `WorkflowScreenService.GenerateValidScreenName` (CoreApi):
 * `ConvertNumbersToWords` → `ReplaceSpecialLettersForTechnicalName` →
 * `Replace("__","_")` → `Replace("_"," ")` → `RemoveIfStartWith("_")` → `Trim()`.
 *
 * Two deliberate deviations, both stated rather than hidden:
 *
 *   • `ConvertNumbersToWords` converts a LEADING number or ordinal to words in a
 *     language it DETECTS at runtime ("1. Onay" → "Birinci Onay" under `tr-TR`). That is
 *     not reproducible here, so a step name starting with a digit returns null and the
 *     pairing falls through to the order phase — reported as unproven instead of matched
 *     against a name we guessed.
 *   • `RemoveIfStartWith("_")` is a no-op at that point in the chain (every `_` has
 *     already become a space), so it is not ported.
 *
 * `Replace("__","_")` is C#'s single left-to-right non-overlapping pass, which
 * `split("__").join("_")` reproduces exactly — three underscores collapse to two, not
 * one, in both languages.
 */
export function generatedScreenNameForStep(stepName: string): string | null {
  const raw: string = stepName ?? "";
  if (raw.trim().length === 0) return "Screen";
  // The number→words branch is language-detected at runtime; do not guess it.
  if (/^\s*\d/.test(raw)) return null;

  const ascii: string = asciiFyLikePlatform(raw);
  let technical: string = "";
  for (let i = 0; i < ascii.length; i++) {
    const c: string = ascii[i];
    const isLetter: boolean = (c >= "A" && c <= "Z") || (c >= "a" && c <= "z");
    const isDigit: boolean = c >= "0" && c <= "9";
    if (i === 0 && !isLetter) {
      technical += "_";
      continue;
    }
    if (!isLetter && !isDigit) {
      technical += "_";
      continue;
    }
    technical += c;
  }
  return technical.split("__").join("_").split("_").join(" ").trim();
}

/**
 * True when `screenName` is the name the platform would have generated for `stepName`.
 *
 * This is the only positive evidence available on this side: the assignment response
 * names the steps it processed but not the screen it created for each
 * (`FormMetadataByStepResponse` carries `ProcessId` / `StepId` / `FormMetadata`), so the
 * screen has to identify itself, and its name is what it has.
 */
export function screenNameMatchesStep(stepName: string, screenName: string): boolean {
  const generated: string | null = generatedScreenNameForStep(stepName);
  if (generated === null) return false;
  const key: string = foldForScreenNameMatch(generated);
  if (key.length === 0) return false;
  return key === foldForScreenNameMatch(screenName);
}

/** True when `links` already maps this step to a non-empty screen id. */
function hasMappedScreen(links: readonly StepScreenLinkLike[], step: StepAwaitingScreen): boolean {
  return links.some(
    (link: StepScreenLinkLike): boolean =>
      String(link.processId ?? "") === String(step.processId) &&
      String(link.stepId ?? "") === String(step.stepId) &&
      String(link.screenId ?? "").length > 0
  );
}

/**
 * Decide which created screen belongs to which step.
 *
 * Three laws, in this order — and the ORDER is the fix, not a detail:
 *
 *  1. **Name first, and ALL of them first.** A created screen whose name is the one the
 *     platform generates from the step's name is that step's screen. This phase runs to
 *     completion over every step before anything else claims a screen, so an unmatched
 *     step can no longer consume a screen that a later step owns by name.
 *  2. **The fallback may never REPLACE a mapping.** A step with no name match still
 *     takes the next unclaimed screen — that is what keeps an unreproducible name from
 *     DROPPING a screen — but only if the step does not already carry one. A step whose
 *     mapping is already satisfied is left exactly as it is: this call created a screen
 *     for some OTHER step, and trading a correct mapping for it is the corruption
 *     described in the header.
 *  3. **The evidence travels with the pair.** `pairedBy: "order"` says the pairing is a
 *     positional guess, so a consumer can refuse to act on the step's behalf instead of
 *     building one step's screen onto another's.
 *
 * Pure: no diagram, no React state, no service. Steps are processed in the order given.
 */
export function pairCreatedScreensWithSteps(
  steps: readonly StepAwaitingScreen[],
  createdScreens: readonly CreatedScreenLike[],
  existingLinks: readonly StepScreenLinkLike[]
): StepScreenPairing[] {
  const pairings: StepScreenPairing[] = [];
  const claimed: Set<string> = new Set<string>();
  const matchedSteps: Set<string> = new Set<string>();

  // Phase 1 — every name match, before any guess.
  for (const step of steps) {
    const byName: CreatedScreenLike | undefined = createdScreens.find(
      (screen: CreatedScreenLike): boolean =>
        !claimed.has(screen.id) && screenNameMatchesStep(step.stepName, screen.name)
    );
    if (byName === undefined) continue;
    claimed.add(byName.id);
    matchedSteps.add(step.stepId);
    pairings.push({
      processId: step.processId,
      stepId: step.stepId,
      stepName: step.stepName,
      screenId: byName.id,
      screenName: byName.name,
      pairedBy: "name"
    });
  }

  // Phase 2 — what is left, positionally, and only for a step that has nothing.
  for (const step of steps) {
    if (matchedSteps.has(step.stepId)) continue;
    if (hasMappedScreen(existingLinks, step)) continue;
    const next: CreatedScreenLike | undefined = createdScreens.find(
      (candidate: CreatedScreenLike): boolean => !claimed.has(candidate.id)
    );
    if (next === undefined) continue;
    claimed.add(next.id);
    pairings.push({
      processId: step.processId,
      stepId: step.stepId,
      stepName: step.stepName,
      screenId: next.id,
      screenName: next.name,
      pairedBy: "order"
    });
  }

  return pairings;
}
